import type { AxiosError } from "axios";
import { cloneDeep } from "lodash-es";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, inject, reactive, toRefs } from "vue";
import { type Router } from "vue-router";

import type { ApiResponse } from "@/common/api/api.service";
import { isValidationAccessResponse } from "@/common/api/api.service";
import SearchService from "@/common/api/search.service";
import WordcompletionService from "@/common/api/wordcompletion.service";
import Dictionaries, { MONOLINGUAL } from "@/common/dictionaries";
import { isIframe } from "@/common/is-iframe";
import RouteHelper from "@/common/route-helper";
import Search from "@/common/search";
import SearchRequestMethods from "@/common/search-request-helper";
import { QUERY_SEARCH_DIRECTION_KEY } from "@/router/query-params";
import type { Dictionary, Level } from "@/types/dictionary";
import { type WordCompletionItem } from "@/types/search";
import type { State } from "@/types/search-store";

import { useFeatureFlagsStore, useLanguageLevelStore, useSettingsStore } from ".";

const defaultState: State = {
  searchState: "idle",
  articleTab: undefined,
  searchResult: [],
  searchDictionaryId: undefined,
  searchDirection: undefined,
  searchDirectionsAvailable: [],
  searchSignature: undefined,
  searchId: "",
  searchWord: "",
  searchedWord: "",
  adjustedSearchLevel: undefined,
  wordCompletionListSignature: undefined,
  wordCompletionList: [],
  isWordCompletionOpen: false
};

const getSearchParameters = (
  state: State,
  filteredMethodIds: SearchRequestMethods | undefined = undefined
) => {
  const languageLevelStore = useLanguageLevelStore();

  const levelState = state.searchDictionaryId
    ? Dictionaries.getLevelFromDictionaryId(state.searchDictionaryId)
    : languageLevelStore.activeLanguageLevel;

  const direction = state.searchDirection ?? "";
  const level = levelState ?? "beginner";

  if (filteredMethodIds) {
    return {
      direction,
      level,
      searchRequestMethods: filteredMethodIds
    };
  }

  let searchRequestMethods;

  // Monolingual (dada) should search from the lowest level and up,
  // as users are free to choose between all dictionaries, regardless of level.
  // Therefore, we don't jump between dictionaries/methods, but fuzzy in the selected dictionary instead.
  if (state.searchDictionaryId) {
    searchRequestMethods = new SearchRequestMethods();
    searchRequestMethods.add(level, direction, [
      Dictionaries.getDictionaryByDictionaryId(state.searchDictionaryId)?.methodId ?? ""
    ]);
  } else {
    const settingsStore = useSettingsStore();
    const languageGroup = settingsStore.activeLanguageGroup;

    searchRequestMethods =
      Dictionaries.getSearchRequestMethodsFromLanguageGroupAndDirectionAndLevel(
        languageGroup ?? "",
        direction,
        level
      );
  }

  return {
    direction,
    level,
    searchRequestMethods
  };
};

const getSignature = (query: string, searchRequestMethods: SearchRequestMethods) =>
  `${searchRequestMethods.getMethodIds().join("-")}-${query}`;

export const useSearchStore = defineStore("search", () => {
  const state = reactive<State>(cloneDeep(defaultState));

  const router = inject<Router>("$router");

  const searchSourceLanguage = computed(() => state.searchDirection?.substring(0, 2));
  const searchTargetLanguage = computed(() => state.searchDirection?.substring(2, 4));

  const search = async (filteredMethodIds: SearchRequestMethods | undefined = undefined) => {
    if (!router) {
      return;
    }

    const routeHelper = new RouteHelper(router);
    const settingsStore = useSettingsStore();

    const trimmedSearchWord = state.searchWord.trim();
    if (trimmedSearchWord === "") {
      return;
    }

    const { direction, level, searchRequestMethods } = getSearchParameters(
      state,
      filteredMethodIds
    );

    const nextSearchSignature = getSignature(trimmedSearchWord, searchRequestMethods);
    if (nextSearchSignature === state.searchSignature) {
      return;
    }

    state.searchSignature = nextSearchSignature;

    // Reset adjusted values, as a new search has been performed
    state.searchState = "loading";
    state.articleTab = undefined;
    state.adjustedSearchLevel = level;
    state.searchedWord = trimmedSearchWord;

    // As ABC is a SPA, we can rely on document.referrer
    const referrer = isIframe() ? document.referrer : "abc.ordbogen.com";

    let result;
    try {
      result = await SearchService.search(trimmedSearchWord, searchRequestMethods, referrer);
    } catch (e) {
      const error = e as AxiosError<ApiResponse>;

      state.searchResult = [];

      if (error.response && isValidationAccessResponse(error.response, "methodIds")) {
        state.searchState = "unauthorized";
        settingsStore.setActiveLanguageGroup(undefined);

        return Promise.reject();
      }

      state.searchState = "failed";
      return Promise.reject();
    }

    const searchResult = result?.data?.data;

    if (Search.searchResultIsEmpty(searchResult)) {
      state.searchResult = [];
      state.searchState = "emptyResult";
      return;
    }

    const languageGroup = settingsStore.activeLanguageGroup;

    const resultInfo = Search.getSearchResultInfo(
      searchResult,
      languageGroup ?? "",
      direction,
      level
    );

    if (resultInfo === null) {
      state.searchResult = [];
      state.searchState = "emptyResult";
      return;
    }

    state.searchDirection = resultInfo.direction;
    state.adjustedSearchLevel = resultInfo.level as Level;

    if (direction !== state.searchDirection) {
      routeHelper.replaceQueryParam({
        [QUERY_SEARCH_DIRECTION_KEY]: state.searchDirection
      });
    }

    if (Search.searchResultIsFuzzy(searchResult)) {
      state.searchState = "fuzzyResult";
    } else {
      state.searchState = "exactResult";
    }

    state.searchId = searchResult.id;
    state.searchResult = searchResult.result;
  };

  const resetSearchResult = () => {
    state.searchResult = [];
    state.searchSignature = undefined;
    state.searchState = "idle";
    state.articleTab = undefined;

    resetAdjustedSearchParameters();
  };

  const resetSearchParameters = () => {
    const dictionary = getRepresentativeDictionary();

    if (dictionary) {
      const dictionaryId = dictionary.dictionaryId;

      state.searchDictionaryId = dictionary.type === MONOLINGUAL ? dictionaryId : undefined;
      state.searchDirection = Dictionaries.getFirstDirectionByDictionaryId(dictionaryId);
      state.searchDirectionsAvailable = Dictionaries.getDirectionsByDictionaryId(dictionaryId);
    } else {
      state.searchDictionaryId = undefined;
      state.searchDirection = undefined;
      state.searchDirectionsAvailable = [];
    }

    state.searchSignature = "";
    state.searchState = "idle";
  };

  const resetAdjustedSearchParameters = () => {
    state.adjustedSearchLevel = undefined;
  };

  const resetSearchDirectionsAvailable = () => {
    const dictionary = getRepresentativeDictionary();

    if (dictionary) {
      state.searchDirectionsAvailable = Dictionaries.getDirectionsByDictionaryId(
        dictionary.dictionaryId
      );
    } else {
      state.searchDirectionsAvailable = [];
    }
  };

  const getRepresentativeDictionary = (): Dictionary | undefined => {
    const settingsStore = useSettingsStore();
    const languageLevelStore = useLanguageLevelStore();

    return Dictionaries.getFirstDictionaryFromLanguageGroupAndLevel(
      settingsStore.activeLanguageGroup ?? "",
      languageLevelStore.activeLanguageLevel ?? ""
    );
  };

  // Word completion
  const wordCompletion = async (query: string) => {
    const featureFlagsStore = useFeatureFlagsStore();
    if (!featureFlagsStore.wordCompletion && !featureFlagsStore.smartComplete) {
      state.wordCompletionList = [];
      return;
    }

    if (query.length < 3) {
      // We don't do word completion for searches under 3 characters
      state.wordCompletionList = [];
      return;
    }

    const { searchRequestMethods } = getSearchParameters(state);

    const wordCompletionListSignature = getSignature(query, searchRequestMethods);
    if (state.wordCompletionListSignature === wordCompletionListSignature) {
      return;
    }

    try {
      const { data } = await WordcompletionService.suggestions(
        query,
        searchRequestMethods.getMethodIds()
      );

      state.wordCompletionListSignature = wordCompletionListSignature;
      state.wordCompletionList = data.data;
    } catch (e) {
      // Ignore failure gracefully
    }
  };

  const logWordCompletionSelection = ({
    uuid,
    engine,
    index,
    selectedWord
  }: WordCompletionItem) => {
    try {
      // No need to await a logging
      WordcompletionService.logSelection(uuid, engine, index, selectedWord);
    } catch (e) {
      // Ignore failure gracefully
    }
  };

  return {
    ...toRefs(state),

    searchSourceLanguage,
    searchTargetLanguage,

    resetSearchResult,
    resetSearchParameters,
    resetAdjustedSearchParameters,
    resetSearchDirectionsAvailable,

    search,

    wordCompletion,
    logWordCompletionSelection
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSearchStore, import.meta.hot));
}
